import { LevelDesignerComponent } from '../../level-designer/level-designer.component';
import { PropertyEditor } from '../property-editor';
import { SelectInput, SelectOption } from './select-input';
import { ApiService } from 'src/app/Services/api.service';
import { OrganizationsService } from 'src/app/Services/organizations.service';

export class ApiSelectInput extends SelectInput {
    private isLoading: boolean = false;
    private hasLoaded: boolean = false;
    private static optionsCache: Map<string, SelectOption[]> = new Map();
    private includeNoneOption: boolean = false;

    constructor(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        value: string,
        isActive: boolean,
        protected apiService: ApiService,
        protected url: string,
        idProperty: string,
        labelProperty: string,
        levelDesigner: LevelDesignerComponent,
        propertyEditor: PropertyEditor,
        private organizationsService: OrganizationsService,
        includeNoneOption: boolean = false,
        allowTextEditing: boolean = false
    ) {
        super(ctx, x, y, width, height, value, isActive, [], levelDesigner, propertyEditor, allowTextEditing);
        this.idProperty = idProperty;
        this.labelProperty = labelProperty;
        this.includeNoneOption = includeNoneOption;
        const cacheKey = this.getCacheKey();
        if (!ApiSelectInput.optionsCache.has(cacheKey)) {
            this.loadOptions();
        } else {
            this.options = ApiSelectInput.optionsCache.get(cacheKey) || [];
            if (this.includeNoneOption) {
                this.options = [{ value: null, label: 'None' }, ...this.options];
            }
            this.hasLoaded = true;

            const matchingOption = this.options.find(opt => (opt.value ?? '').toString() === (this.value ?? '').toString());
            if (matchingOption) {
                this.displayValue = matchingOption.label;
                this.searchText = this.displayValue;
            }

        }
    }

    private getCacheKey(): string {
        return `${this.url}_${this.organizationsService.CurrentOrganization.Id}`;
    }

    override draw(): void {
        super.draw();

        if (this.isLoading) {
            // Draw loading indicator
            this.ctx.save();
            this.ctx.fillStyle = '#666666';
            this.ctx.font = '10px Arial';
            this.ctx.textAlign = 'right';
            this.ctx.fillText('Loading...', this.x + this.width - 25, this.y + this.height / 2);
            this.ctx.restore();
        }
    }

    override handleClick(x: number, y: number): boolean {
        if (!this.hasLoaded && !this.isLoading) {
            this.loadOptions();
        }
        return super.handleClick(x, y);
    }

    protected async loadOptions(): Promise<void> {
        const cacheKey = this.getCacheKey();
        if (this.isLoading || ApiSelectInput.optionsCache.has(cacheKey)) {
            return;
        }

        this.isLoading = true;
        try {
            const response = await this.apiService.Get<any[]>(this.url);
            const options = response.map(item => ({
                value: item[this.idProperty],
                label: item[this.labelProperty]
            }));
            
            ApiSelectInput.optionsCache.set(cacheKey, options);
            this.options = this.includeNoneOption
                ? [{ value: null, label: 'None' }, ...options]
                : options;
            this.hasLoaded = true;

            // Update display value if there's a matching option
            const matchingOption = this.options.find(opt => (opt.value ?? '').toString() === (this.value ?? '').toString());
            if (matchingOption) {
                this.displayValue = matchingOption.label;
                this.searchText = this.displayValue;
            }

            if (this.isActive) {
                this.dropdownVisible = true;
            }
        } catch (error) {
            console.error('Failed to load select options:', error);
            this.options = [];
        } finally {
            this.isLoading = false;
            this.levelDesigner.redraw();
        }
    }

    override focus(): void {
        super.focus();
        if (!this.hasLoaded && !this.isLoading) {
            this.loadOptions();
        }
    }

    override blur(): void {
        super.blur();
        this.isLoading = false;
    }

    // Clear cache for specific organization
    static clearCache(url?: string, organizationId?: string) {
        if (url && organizationId) {
            ApiSelectInput.optionsCache.delete(`${url}_${organizationId}`);
        } else {
            ApiSelectInput.optionsCache.clear();
        }
    }
} 
